import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase/client'
import { FileText, CheckCircle, Download, Eye, Edit, Filter, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import { StatusIcon } from '@/components/ui'
import { generatePDF as generateMTPDF } from '@/services/mtPDFTemplate'
import { generatePDF as generateETPDF } from '@/services/etPDFTemplate'
import type { MTReportData } from '@/types/mtTypes'
import type { ETReportData } from '@/types/etTypes'
import { hasAdminAccess } from '@/utils/permissions'

interface ControlCard {
  id: string
  equipment_id: string
  file_name: string
  storage_path: string
  created_at: string
  equipment_serial: string | null
  equipment_name: string
  company_name: string
  inspection_status: string | null
}

interface Report {
  id: string
  order_number: string
  unit_id: string
  test_date: string
  test_type: string
  status: 'Accepted' | 'Not Accepted'
  issued_by: string
  verified_by: string | null
  file_path: string
  report_code: string | null
  company_id: string
  created_at: string
  created_by: string
  approved_at: string | null
  approved_by: string | null
  report_data: MTReportData | ETReportData | null
  company_name?: string
}

const ReportsPage = () => {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<{
    id: string
    full_name: string
    role: 'admin' | 'inspector' | 'client'
    supervisor?: boolean
  } | null>(null)
  const [activeTab, setActiveTab] = useState<'ndt' | 'control_cards'>('ndt')
  const [controlCards, setControlCards] = useState<ControlCard[]>([])
  const [controlCardsLoading, setControlCardsLoading] = useState(false)
  const CONTROL_CARD_LIMIT = 100
  const [processingReportId, setProcessingReportId] = useState<string | null>(null)
  const [editingReport, setEditingReport] = useState<Report | null>(null)
  const [editForm, setEditForm] = useState<MTReportData | ETReportData | null>(null)
  
  // Filter states
  const [filterMonth, setFilterMonth] = useState<string>('')
  const [filterCompany, setFilterCompany] = useState<string>('')
  const [filterInspector, setFilterInspector] = useState<string>('')
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([])
  const [inspectors, setInspectors] = useState<Array<{ id: string; full_name: string }>>([])
  const [showFilters, setShowFilters] = useState(false)

  // Pagination state
  const PAGE_SIZE = 10
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    fetchCurrentUser()
  }, [])

  useEffect(() => {
    if (currentUser) {
      fetchReports()
      fetchFilterData()
      fetchControlCards()
    }
  }, [currentUser])

  useEffect(() => {
    if (currentUser && activeTab === 'control_cards') fetchControlCards()
  }, [activeTab])

  useEffect(() => {
    if (currentUser) {
      setCurrentPage(1)
      fetchReports(1)
      fetchControlCards()
    }
  }, [filterMonth, filterCompany, filterInspector])

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name, role, supervisor')
        .eq('id', user.id)
        .single()

      if (profile) {
        setCurrentUser(profile)
      }
    } catch (error) {
      console.error('Error fetching user:', error)
    }
  }

  const fetchFilterData = async () => {
    try {
      // Fetch companies
      const { data: companiesData } = await supabase
        .from('companies')
        .select('id, name')
        .order('name')

      if (companiesData) {
        setCompanies(companiesData)
      }

      // Fetch inspectors (for admins and supervisors)
      if (hasAdminAccess(currentUser)) {
        const { data: inspectorsData } = await supabase
          .from('profiles')
          .select('id, full_name')
          .eq('role', 'inspector')
          .order('full_name')

        if (inspectorsData) {
          setInspectors(inspectorsData)
        }
      }
    } catch (error) {
      console.error('Error fetching filter data:', error)
    }
  }

  const fetchControlCards = async () => {
    if (!currentUser) return
    setControlCardsLoading(true)
    try {
      // Fetch documents only — embedding equipment_v2 makes created_at ambiguous
      // (both tables have that column), so Postgres can sort by equipment.created_at
      // and hide recent August uploads behind June equipment imports.
      let query = supabase
        .from('equipment_documents')
        .select('id, equipment_id, file_name, storage_path, created_at, inspection_status, uploaded_by')
        .eq('document_type', 'control_card')
        .eq('is_current', true)
        .order('created_at', { ascending: false })
        .limit(CONTROL_CARD_LIMIT)

      if (filterInspector) {
        const inspectorId = inspectors.find(i => i.full_name === filterInspector)?.id
        if (inspectorId) query = query.eq('uploaded_by', inspectorId)
      }

      if (filterMonth) {
        const startDate = `${filterMonth}-01`
        const endDate = new Date(filterMonth + '-01')
        endDate.setMonth(endDate.getMonth() + 1)
        query = query.gte('created_at', startDate).lt('created_at', endDate.toISOString().split('T')[0])
      }

      const { data: docs, error } = await query
      if (error) throw error

      const rows = docs || []
      const equipmentIds = [...new Set(rows.map(d => d.equipment_id).filter(Boolean))]

      const equipmentMap: Record<string, {
        name: string
        serial: string | null
        company_id: string | null
        company_name: string
        inspection_status: string | null
      }> = {}

      if (equipmentIds.length > 0) {
        const { data: equipmentRows, error: equipmentError } = await supabase
          .from('equipment_v2')
          .select('id, name, serial_number, company_id, inspection_status, companies(name)')
          .in('id', equipmentIds)

        if (equipmentError) throw equipmentError

        for (const eq of equipmentRows || []) {
          const company = Array.isArray(eq.companies) ? eq.companies[0] : eq.companies
          equipmentMap[eq.id] = {
            name: eq.name || '',
            serial: eq.serial_number || null,
            company_id: eq.company_id || null,
            company_name: (company as { name?: string } | null)?.name || '—',
            inspection_status: eq.inspection_status || null,
          }
        }
      }

      setControlCards(
        rows
          .filter(row => !filterCompany || equipmentMap[row.equipment_id]?.company_id === filterCompany)
          .map(row => {
            const equipment = equipmentMap[row.equipment_id]
            const serial = equipment?.serial || null
            return {
              id: row.id,
              equipment_id: row.equipment_id,
              file_name: row.file_name,
              storage_path: row.storage_path,
              created_at: row.created_at,
              equipment_serial: serial,
              equipment_name: (equipment?.name && equipment.name.trim()) || serial || '—',
              company_name: equipment?.company_name || '—',
              inspection_status: row.inspection_status || equipment?.inspection_status || null,
            }
          })
      )
    } catch (err) {
      console.error('Error fetching control cards:', err)
      setControlCards([])
    } finally {
      setControlCardsLoading(false)
    }
  }

  const fetchReports = async (page?: number) => {
    const activePage = page ?? currentPage
    try {
      setLoading(true)

      const offset = (activePage - 1) * PAGE_SIZE

      // Build query based on user role
      let query = supabase
        .from('ndt_reports')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })

      // If not admin/supervisor, only show reports issued by this inspector
      if (!hasAdminAccess(currentUser)) {
        query = query.eq('issued_by', currentUser?.full_name)
      }

      // Apply filters
      if (filterMonth) {
        // Filter by month (YYYY-MM format)
        const startDate = `${filterMonth}-01`
        const endDate = new Date(filterMonth + '-01')
        endDate.setMonth(endDate.getMonth() + 1)
        const endDateStr = endDate.toISOString().split('T')[0]
        
        query = query.gte('test_date', startDate).lt('test_date', endDateStr)
      }

      if (filterCompany) {
        query = query.eq('company_id', filterCompany)
      }

      if (filterInspector && hasAdminAccess(currentUser)) {
        query = query.eq('issued_by', filterInspector)
      }

      const { data: reportsData, error: reportsError, count } = await query
        .range(offset, offset + PAGE_SIZE - 1)

      if (reportsError) throw reportsError

      setTotalCount(count ?? 0)

      // Fetch company names separately to avoid RLS issues
      const companyIds = [...new Set(reportsData?.map(r => r.company_id).filter(Boolean))]
      let companiesMap: Record<string, string> = {}

      if (companyIds.length > 0) {
        const { data: companiesData } = await supabase
          .from('companies')
          .select('id, name')
          .in('id', companyIds)

        if (companiesData) {
          companiesMap = Object.fromEntries(
            companiesData.map(c => [c.id, c.name])
          )
        }
      }

      const reportsWithCompany = reportsData?.map(report => ({
        ...report,
        company_name: companiesMap[report.company_id] || 'Unknown'
      })) || []

      setReports(reportsWithCompany)
    } catch (error) {
      console.error('Error fetching reports:', error)
      alert('Failed to load reports')
    } finally {
      setLoading(false)
    }
  }

  const handleViewReport = async (report: Report) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('reports')
        .createSignedUrl(report.file_path, 3600)

      if (error) throw error
      if (!data?.signedUrl) throw new Error('No signed URL returned')

      window.open(data.signedUrl, '_blank')
    } catch (error) {
      console.error('Error viewing report:', error)
      alert('Failed to open report')
    }
  }

  const handleDownloadReport = async (report: Report) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('reports')
        .download(report.file_path)

      if (error) throw error

      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url
      a.download = `${report.order_number}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading report:', error)
      alert('Failed to download report')
    }
  }

  const handleApproveReport = async (report: Report) => {
    if (!currentUser || !hasAdminAccess(currentUser)) {
      alert('Only admins and supervisors can approve reports')
      return
    }

    const confirmApproval = window.confirm(
      `Are you sure you want to approve this report?\n\n` +
      `Order: ${report.order_number}\n` +
      `Test Type: ${report.test_type}\n` +
      `Issued by: ${report.issued_by}\n\n` +
      `This will regenerate the PDF with your signature and approval.`
    )

    if (!confirmApproval) return

    setProcessingReportId(report.id)

    try {
      // Fetch the original report data to regenerate it
      if (report.test_type === 'MT' || report.test_type === 'ET') {
        await regenerateAndApproveReport(report)
      } else {
        alert(`Approval is currently only supported for MT and ET reports`)
      }
    } catch (error) {
      console.error('Error approving report:', error)
      alert('Failed to approve report. Please try again.')
    } finally {
      setProcessingReportId(null)
    }
  }

  const regenerateAndApproveReport = async (report: Report) => {
    try {
      // For MT reports, we need to fetch additional data and regenerate the PDF
      // First, get the full report data from ndt_reports_data table if it exists
      // For now, we'll update the database and notify user that they need to regenerate manually
      
      const approvalDate = new Date().toISOString()
      
      // Update the report with approval information in the database
      const { error: updateError } = await supabase
        .from('ndt_reports')
        .update({
          approved_by: currentUser?.id,
          approved_at: approvalDate,
          verified_by: currentUser?.full_name
        })
        .eq('id', report.id)

      if (updateError) throw updateError

      // Regenerate the PDF with approval info
      if (report.test_type === 'MT') {
        await regenerateMTPDFWithApproval(report, approvalDate)
      } else if (report.test_type === 'ET') {
        await regenerateETPDFWithApproval(report, approvalDate)
      }

      alert('Report approved successfully! The PDF has been regenerated with your signature.')
      
      // Refresh the reports list
      fetchReports()
    } catch (error) {
      console.error('Error in regenerateAndApproveReport:', error)
      throw error
    }
  }

  const regenerateMTPDFWithApproval = async (report: Report, approvalDate: string) => {
    try {
      // Use stored report_data if available, otherwise create minimal version
      let mtData: MTReportData
      
      if (report.report_data && 'objects' in report.report_data) {
        // Use the stored original data
        mtData = {
          ...(report.report_data as MTReportData),
          // Add approval information
          approvedBy: currentUser?.full_name,
          approvedAt: approvalDate
        }
      } else {
        // Fallback for old reports without stored data
        mtData = {
          testDate: report.test_date,
          reportNumber: report.order_number.replace('MT-', '').split('.')[0],
          objects: report.unit_id ? report.unit_id.split(', ') : [],
          objectDescription: report.unit_id || 'See original report',
          issuedBy: report.issued_by,
          status: report.status,
          surfaceCondition: 'Cleaned',
          equipmentSettings: {
            magnetizingEquipment: 'Yoke, Magnaflux Y-1, sn: 2291',
            lighting: '1500 uW/cm2 / 1100 LUX',
            demagnetization: 'No',
            otherEquipment: 'Castrol Strips Type A (type II / Silver)',
            current: 'AC / 220v',
            fieldStrength: '4,75 Kg – 15 cm',
            magneticInk: 'Fluxo 6',
            inkBatchNo: 'L231106-1',
            contrast: 'UV – Light, 365 Nm',
            contrastBatchNo: 'M3Zb16332'
          },
          certificationNumber: '---',
          sequenceNumber: report.order_number.split('.')[1] || '001',
          extentOfExamination: '100',
          material: 'Carbon steel',
          testProcedure: 'MT DNV-CG-0051',
          testAcceptance: 'DNV-CG-0051 Level 1',
          referenceStandards: '---',
          company: report.company_id,
          report_code: report.report_code,
          // Add approval information
          approvedBy: currentUser?.full_name,
          approvedAt: approvalDate
        }
      }

      // Generate the new PDF with approval info
      const pdfBlob = await generateMTPDF(mtData)

      // Upload the new PDF, replacing the old one
      const { error: uploadError } = await supabase
        .storage
        .from('reports')
        .update(report.file_path, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) {
        console.error('Error uploading approved PDF:', uploadError)
        throw uploadError
      }

      console.log('PDF regenerated successfully with approval information')
    } catch (error) {
      console.error('Error regenerating MT PDF:', error)
      throw error
    }
  }

  const regenerateETPDFWithApproval = async (report: Report, approvalDate: string) => {
    try {
      // Use stored report_data if available
      let etData: ETReportData
      
      if (report.report_data && 'probeSettings' in report.report_data) {
        // Use the stored original data
        etData = {
          ...report.report_data as ETReportData,
          // Add approval information
          approvedBy: currentUser?.full_name,
          approvedAt: approvalDate
        }
      } else {
        // Fallback for old reports without stored data
        const DEFAULT_ET_PROBE_SETTINGS = [
          { type: 'Absolute', frequency: '500', gain: '21', phase: '120', enforcement: '0' },
          { type: 'Bridge', frequency: '100', gain: '55', phase: '190', enforcement: '0' }
        ]
        
        etData = {
          testDate: report.test_date,
          reportNumber: report.order_number.replace('ET-', '').split('.')[0],
          unitId: report.unit_id || 'N/A',
          slingId: '',
          isChainSling: false,
          issuedBy: report.issued_by,
          status: report.status,
          objectType: 'POU',
          material: 'Carbon steel',
          probeSettings: DEFAULT_ET_PROBE_SETTINGS,
          certificationNumber: '---',
          sequenceNumber: report.order_number.split('.')[1] || '001',
          company: report.company_id,
          report_code: report.report_code,
          attachedImages: [],
          // Add approval information
          approvedBy: currentUser?.full_name,
          approvedAt: approvalDate
        }
      }

      // Generate the new PDF with approval info
      const pdfBlob = await generateETPDF(etData)

      // Upload the new PDF, replacing the old one
      const { error: uploadError } = await supabase
        .storage
        .from('reports')
        .update(report.file_path, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) {
        console.error('Error uploading approved PDF:', uploadError)
        throw uploadError
      }

      console.log('ET PDF regenerated successfully with approval information')
    } catch (error) {
      console.error('Error regenerating ET PDF:', error)
      throw error
    }
  }

  const handleEditReport = (report: Report) => {
    if (!report.report_data) {
      alert('This report was created before the edit feature was added and cannot be edited. Only newly created reports can be edited.')
      return
    }
    
    setEditingReport(report)
    setEditForm(report.report_data)
  }

  const handleSaveEdit = async () => {
    if (!editingReport || !editForm) return

    setProcessingReportId(editingReport.id)

    try {
      // Get unit_id based on report type
      let unitIdForDb: string
      if ('objects' in editForm) {
        // MT report
        const objectsList = editForm.objects?.filter(o => o.trim()).join(', ') || editForm.objectDescription
        unitIdForDb = objectsList
      } else {
        // ET report
        unitIdForDb = editForm.unitId || editForm.slingId || 'N/A'
      }

      // Update the report in database
      const { error: updateError } = await supabase
        .from('ndt_reports')
        .update({
          unit_id: unitIdForDb,
          status: editForm.status,
          test_date: editForm.testDate,
          report_data: editForm,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingReport.id)

      if (updateError) throw updateError

      // Regenerate the PDF with updated information
      if (editingReport.test_type === 'MT' && 'objects' in editForm) {
        await regenerateMTPDFWithUpdates(editingReport, editForm)
      } else if (editingReport.test_type === 'ET' && 'unitId' in editForm) {
        await regenerateETPDFWithUpdates(editingReport, editForm)
      }

      alert('Report updated successfully!')
      setEditingReport(null)
      setEditForm(null)
      fetchReports()
    } catch (error) {
      console.error('Error updating report:', error)
      alert('Failed to update report. Please try again.')
    } finally {
      setProcessingReportId(null)
    }
  }

  const regenerateMTPDFWithUpdates = async (report: Report, mtData: MTReportData) => {
    try {
      const pdfBlob = await generateMTPDF(mtData)

      const { error: uploadError } = await supabase
        .storage
        .from('reports')
        .update(report.file_path, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) throw uploadError

      console.log('PDF regenerated successfully with updates')
    } catch (error) {
      console.error('Error regenerating PDF:', error)
      throw error
    }
  }

  const regenerateETPDFWithUpdates = async (report: Report, etData: ETReportData) => {
    try {
      const pdfBlob = await generateETPDF(etData)

      const { error: uploadError } = await supabase
        .storage
        .from('reports')
        .update(report.file_path, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) throw uploadError

      console.log('ET PDF regenerated successfully with updates')
    } catch (error) {
      console.error('Error regenerating ET PDF:', error)
      throw error
    }
  }

  const handleDeleteReport = async (report: Report) => {
    if (!currentUser || !hasAdminAccess(currentUser)) {
      alert('Only admins and supervisors can delete reports')
      return
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete this report?\n\n` +
      `Order: ${report.order_number}\n` +
      `Test Type: ${report.test_type}\n` +
      `Issued by: ${report.issued_by}\n\n` +
      `This action cannot be undone!`
    )

    if (!confirmDelete) return

    setProcessingReportId(report.id)

    try {
      // Delete the PDF file from storage
      const { error: storageError } = await supabase
        .storage
        .from('reports')
        .remove([report.file_path])

      if (storageError) {
        console.error('Error deleting PDF from storage:', storageError)
        // Continue with database deletion even if file deletion fails
      }

      // Delete the report record from database
      const { error: dbError } = await supabase
        .from('ndt_reports')
        .delete()
        .eq('id', report.id)

      if (dbError) throw dbError

      alert('Report deleted successfully!')
      
      // Refresh the reports list
      fetchReports()
    } catch (error) {
      console.error('Error deleting report:', error)
      alert('Failed to delete report. Please try again.')
    } finally {
      setProcessingReportId(null)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    fetchReports(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const clearFilters = () => {
    setFilterMonth('')
    setFilterCompany('')
    setFilterInspector('')
  }

  return (
    <div className="min-h-screen p-6 sm:p-8" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-end mb-5">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-5 py-3 border rounded-md text-base font-medium transition-colors min-h-[48px] ${
              showFilters
                ? 'border-[#1E3A5F] text-[#1E3A5F] bg-white shadow-sm'
                : 'border-slate-300 text-slate-600 bg-white hover:border-slate-400'
            }`}
          >
            <Filter size={18} />
            <span>Filter</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
          {(['ndt', 'control_cards'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeTab === tab
                  ? 'bg-white text-[#1E3A5F] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab === 'ndt' ? 'NDT-rapporter' : 'Kontrollkort'}
            </button>
          ))}
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
              Filtrer rapporter
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Måned
                </label>
                <input
                  type="month"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 bg-white transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Selskap
                </label>
                <select
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 bg-white transition-colors"
                >
                  <option value="">Alle selskaper</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              {hasAdminAccess(currentUser) && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Inspektør
                  </label>
                  <select
                    value={filterInspector}
                    onChange={(e) => setFilterInspector(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 bg-white transition-colors"
                  >
                    <option value="">Alle inspektører</option>
                    {inspectors.map(inspector => (
                      <option key={inspector.id} value={inspector.full_name}>
                        {inspector.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <div className="flex flex-wrap gap-2">
                {filterMonth && (
                  <span className="px-2.5 py-1 bg-[#1E3A5F]/8 text-[#1E3A5F] border border-[#1E3A5F]/20 rounded-full text-xs font-medium">
                    {new Date(filterMonth + '-01').toLocaleDateString('nb-NO', { year: 'numeric', month: 'long' })}
                  </span>
                )}
                {filterCompany && (
                  <span className="px-2.5 py-1 bg-[#1E3A5F]/8 text-[#1E3A5F] border border-[#1E3A5F]/20 rounded-full text-xs font-medium">
                    {companies.find(c => c.id === filterCompany)?.name}
                  </span>
                )}
                {filterInspector && (
                  <span className="px-2.5 py-1 bg-[#1E3A5F]/8 text-[#1E3A5F] border border-[#1E3A5F]/20 rounded-full text-xs font-medium">
                    {filterInspector}
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="text-sm text-slate-500 hover:text-[#1E3A5F] transition-colors font-medium"
              >
                Nullstill
              </button>
            </div>
          </div>
        )}

        {/* Control Cards Tab */}
        {activeTab === 'control_cards' && (
          controlCardsLoading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="w-10 h-10 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin mb-3" />
              <span className="text-sm text-slate-500">Laster kontrollkort...</span>
            </div>
          ) : controlCards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <FileText size={40} className="text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 font-medium">Ingen kontrollkort funnet</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-400 font-medium">
                  Siste {controlCards.length} opplastede kontrollkort
                </p>
              </div>
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <div className="divide-y divide-slate-100">
                {controlCards.map(cc => (
                  <div
                    key={cc.id}
                    className="flex flex-col gap-3 px-5 py-4 hover:bg-slate-50 transition-colors sm:flex-row sm:items-start sm:gap-6"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold text-slate-800 truncate" title={cc.equipment_name}>
                        {cc.equipment_name}
                      </p>
                      <p className="text-sm text-slate-600 mt-1 truncate">{cc.company_name}</p>
                      {cc.equipment_serial && (
                        <p className="text-sm text-slate-400 mt-0.5 truncate">SN: {cc.equipment_serial}</p>
                      )}
                      <div className="mt-2.5">
                        {cc.inspection_status ? (
                          <StatusIcon status={cc.inspection_status} size="lg" />
                        ) : (
                          <span className="text-sm text-slate-300">—</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end sm:justify-start sm:shrink-0">
                      <p className="text-sm font-medium text-slate-500 whitespace-nowrap tabular-nums">
                        {formatDate(cc.created_at)}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={async () => {
                            const { data } = await supabase.storage.from('documentation').createSignedUrl(cc.storage_path, 3600)
                            if (data?.signedUrl) window.open(data.signedUrl, '_blank')
                          }}
                          className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-100 rounded-md transition-colors"
                          title="Vis kontrollkort"
                        >
                          <Eye size={19} />
                        </button>
                        <button
                          onClick={async () => {
                            const { data } = await supabase.storage.from('documentation').download(cc.storage_path)
                            if (data) {
                              const url = URL.createObjectURL(data)
                              const a = document.createElement('a')
                              a.href = url; a.download = cc.file_name; a.click()
                              URL.revokeObjectURL(url)
                            }
                          }}
                          className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-100 rounded-md transition-colors"
                          title="Last ned"
                        >
                          <Download size={19} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>
          )
        )}

        {/* NDT Reports Tab */}
        {activeTab === 'ndt' && (loading ? (
          <div className="flex flex-col justify-center items-center py-20">
            <div className="w-10 h-10 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin mb-3" />
            <span className="text-sm text-slate-500">Laster rapporter...</span>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FileText size={40} className="text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">Ingen rapporter funnet</p>
          </div>
        ) : (
          <div>
            {/* Results summary + count */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-400 font-medium">
                {totalCount === 0
                  ? 'Ingen rapporter'
                  : `Viser ${((currentPage - 1) * PAGE_SIZE) + 1}–${Math.min(currentPage * PAGE_SIZE, totalCount)} av ${totalCount}`}
              </p>
            </div>

            {/* Report rows */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <div className="divide-y divide-slate-100">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className="flex flex-col gap-3 px-5 py-4 hover:bg-slate-50 transition-colors sm:flex-row sm:items-start sm:gap-6"
                  >
                    <div className="min-w-0 flex-1">
                      {report.order_number && (
                        <p className="text-base font-semibold text-[#1E3A5F] truncate" title={report.order_number}>
                          {report.order_number}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mt-1 truncate">{report.company_name}</p>
                      <p className="text-sm text-slate-400 mt-0.5 truncate">
                        {report.issued_by}
                        {report.unit_id ? ` · ID: ${report.unit_id}` : ''}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mt-2.5">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          report.status === 'Accepted'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}>
                          {report.status === 'Accepted' ? 'Godkjent' : 'Ikke godkjent'}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                          {report.test_type}
                        </span>
                        {report.approved_by && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#0D9488]/8 text-[#0D9488] border border-[#0D9488]/20">
                            <CheckCircle size={11} />
                            Verifisert
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end sm:justify-start sm:shrink-0">
                      <p className="text-sm font-medium text-slate-500 whitespace-nowrap tabular-nums">
                        {formatDate(report.test_date)}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleViewReport(report)}
                          className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-100 rounded-md transition-colors"
                          title="Vis rapport"
                        >
                          <Eye size={19} />
                        </button>
                        <button
                          onClick={() => handleDownloadReport(report)}
                          className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-100 rounded-md transition-colors"
                          title="Last ned PDF"
                        >
                          <Download size={19} />
                        </button>
                        {hasAdminAccess(currentUser) && !report.approved_by && (
                          <button
                            onClick={() => handleEditReport(report)}
                            className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-100 rounded-md transition-colors"
                            title="Rediger"
                          >
                            <Edit size={19} />
                          </button>
                        )}
                        {hasAdminAccess(currentUser) && !report.approved_by && (
                          <button
                            onClick={() => handleApproveReport(report)}
                            disabled={processingReportId === report.id}
                            className="w-11 h-11 flex items-center justify-center bg-[#0D9488] text-white rounded-md hover:bg-[#0B7A70] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            title="Godkjenn rapport"
                          >
                            {processingReportId === report.id
                              ? <span className="text-xs font-bold">…</span>
                              : <CheckCircle size={19} />
                            }
                          </button>
                        )}
                        {hasAdminAccess(currentUser) && (
                          <button
                            onClick={() => handleDeleteReport(report)}
                            disabled={processingReportId === report.id}
                            className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Slett rapport"
                          >
                            <Trash2 size={19} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalCount > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4 px-1">
                <p className="text-sm text-slate-400 font-medium">
                  Side {currentPage} av {Math.ceil(totalCount / PAGE_SIZE)}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-3 text-slate-400 hover:text-[#1E3A5F] hover:bg-white border border-transparent hover:border-slate-200 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ minWidth: 44, minHeight: 44 }}
                    aria-label="Forrige side"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  {(() => {
                    const totalPages = Math.ceil(totalCount / PAGE_SIZE)
                    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)

                    return pages.reduce<(number | string)[]>((acc, p, i, arr) => {
                      if (i > 0 && (arr[i - 1] as number) !== p - 1) acc.push(`ellipsis-${p}`)
                      acc.push(p)
                      return acc
                    }, []).map((item) =>
                      typeof item === 'string' ? (
                        <span key={item} className="w-10 text-center text-slate-400 text-base select-none">…</span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => handlePageChange(item)}
                          className={`w-11 h-11 rounded-md text-base font-semibold transition-colors ${
                            currentPage === item
                              ? 'bg-[#1E3A5F] text-white shadow-sm'
                              : 'text-slate-600 hover:bg-white hover:border-slate-200 border border-transparent'
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )
                  })()}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === Math.ceil(totalCount / PAGE_SIZE)}
                    className="p-3 text-slate-400 hover:text-[#1E3A5F] hover:bg-white border border-transparent hover:border-slate-200 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ minWidth: 44, minHeight: 44 }}
                    aria-label="Neste side"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Edit Modal - MT Reports */}
        {editingReport && editForm && editingReport.test_type === 'MT' && 'objects' in editForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-4xl w-full p-4 sm:p-8 my-4 sm:my-8 shadow-xl border border-stone-200">
              <h2 className="text-xl sm:text-3xl font-medium text-[#1E3A5F] mb-4 sm:mb-6 truncate">
                Edit MT Report - {editingReport.order_number}
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 max-h-[55vh] sm:max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Test Date</label>
                  <input
                    type="date"
                    value={editForm.testDate}
                    onChange={(e) => setEditForm({ ...editForm, testDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  >
                    <option value="Accepted">Accepted</option>
                    <option value="Not Accepted">Not Accepted</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Object Description</label>
                  <input
                    type="text"
                    value={editForm.objectDescription}
                    onChange={(e) => setEditForm({ ...editForm, objectDescription: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Drawing</label>
                  <input
                    type="text"
                    value={editForm.drawing || ''}
                    onChange={(e) => setEditForm({ ...editForm, drawing: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Diameter</label>
                  <input
                    type="text"
                    value={editForm.diameter || ''}
                    onChange={(e) => setEditForm({ ...editForm, diameter: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Thickness</label>
                  <input
                    type="text"
                    value={editForm.thickness || ''}
                    onChange={(e) => setEditForm({ ...editForm, thickness: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Extent of Examination</label>
                  <input
                    type="text"
                    value={editForm.extentOfExamination}
                    onChange={(e) => setEditForm({ ...editForm, extentOfExamination: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Comments</label>
                  <textarea
                    value={editForm.comments || ''}
                    onChange={(e) => setEditForm({ ...editForm, comments: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => { setEditingReport(null); setEditForm(null); }}
                  className="flex-1 px-6 py-3 border-2 border-stone-300 text-stone-600 rounded-xl hover:border-[#1E3A5F] hover:text-[#1E3A5F] transition-colors font-medium"
                  disabled={processingReportId === editingReport.id}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={processingReportId === editingReport.id}
                  className="flex-1 px-6 py-3 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#152C4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md"
                >
                  {processingReportId === editingReport.id ? 'Saving...' : 'Save & Regenerate PDF'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal - ET Reports */}
        {editingReport && editForm && editingReport.test_type === 'ET' && 'unitId' in editForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-4xl w-full p-4 sm:p-8 my-4 sm:my-8 shadow-xl border border-stone-200">
              <h2 className="text-xl sm:text-3xl font-medium text-[#1E3A5F] mb-4 sm:mb-6 truncate">
                Edit ET Report - {editingReport.order_number}
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 max-h-[55vh] sm:max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Test Date</label>
                  <input
                    type="date"
                    value={editForm.testDate}
                    onChange={(e) => setEditForm({ ...editForm, testDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  >
                    <option value="Accepted">Accepted</option>
                    <option value="Not Accepted">Not Accepted</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Unit ID</label>
                  <input
                    type="text"
                    value={editForm.unitId || ''}
                    onChange={(e) => setEditForm({ ...editForm, unitId: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Sling ID (Optional)</label>
                  <input
                    type="text"
                    value={editForm.slingId || ''}
                    onChange={(e) => setEditForm({ ...editForm, slingId: e.target.value })}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.isChainSling || false}
                      onChange={(e) => setEditForm({ ...editForm, isChainSling: e.target.checked })}
                      className="w-5 h-5 accent-[#1E3A5F]"
                    />
                    Chain Sling
                  </label>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Comments</label>
                  <textarea
                    value={editForm.comments || ''}
                    onChange={(e) => setEditForm({ ...editForm, comments: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#1E3A5F] text-stone-800 bg-white"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => { setEditingReport(null); setEditForm(null); }}
                  className="flex-1 px-6 py-3 border-2 border-stone-300 text-stone-600 rounded-xl hover:border-[#1E3A5F] hover:text-[#1E3A5F] transition-colors font-medium"
                  disabled={processingReportId === editingReport.id}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={processingReportId === editingReport.id}
                  className="flex-1 px-6 py-3 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#152C4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md"
                >
                  {processingReportId === editingReport.id ? 'Saving...' : 'Save & Regenerate PDF'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReportsPage

